import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import type { FilterOptions } from '@lyra/shared';
import { PROVISION_FAILED_REASON } from '@lyra/shared';
import { Download } from 'lucide-react';
import { DocTop } from '@/components/shell/DocTop';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { LiveRegion } from '@/components/a11y/LiveRegion';
import { formatCurrencyEurRo, formatDateTimeRo, formatNumberRo } from '@/lib/format';
import { useActiveSession, useWallet } from '@/lib/walletHooks';
import { shouldShowProvisioningBanner } from '@/lib/instanceState';
import { useMe } from '@/lib/useMe';
import { canSeeBilling } from '@/lib/permissions';
import { useFinalizeRun, useRestartRunWithInstance, useRun } from './api/runHooks';
import { buildRowsFromRun, filterAndSortRows, groupResults } from './lib/rows';
import { TABLE_BUCKET_LABELS } from './lib/labels';
import { runActualCostEur } from './lib/timing';
import { runProgress } from './lib/progress';
import { LiveProgress } from './LiveProgress';
import { ResultsTable } from './ResultsTable';
import { VerdictLine } from './VerdictLine';
import { WizardShell } from './wizard/WizardShell';

/** Reduced to the four Direction-B result buckets (matches the summary chips
 *  below) rather than every `FilterOptions['status']` union value — the full
 *  per-`workflowStatus` values remain reachable via the confidence/search
 *  controls and are not needed as top-level tabs for this task. */
const STATUS_TABS: Array<{ value: FilterOptions['status']; label: string }> = [
  { value: 'all', label: 'Toate' },
  { value: 'correct', label: 'Rezolvate' },
  { value: 'review_pending', label: 'De verificat' },
  { value: 'unresolved', label: TABLE_BUCKET_LABELS.faraCorespondent },
  { value: 'error', label: 'Erori' },
];

const CONFIDENCE_OPTIONS: Array<{ value: FilterOptions['confidenceRange']; label: string }> = [
  { value: 'all', label: 'Toate încrederile' },
  { value: 'high', label: 'Încredere ridicată (≥90%)' },
  { value: 'medium', label: 'Încredere medie (70–89%)' },
  { value: 'low', label: 'Încredere scăzută (sub 70%)' },
];

const SORT_OPTIONS: Array<{ value: FilterOptions['sortBy']; label: string }> = [
  { value: 'code', label: 'Cod sursă' },
  { value: 'name', label: 'Denumire' },
  { value: 'codMedoc', label: 'Cod MEDOC' },
  { value: 'confidence', label: 'Încredere' },
];

/** Matches Task 5's `base` defaults exactly (brief). */
const DEFAULT_FILTERS: FilterOptions = {
  searchTerm: '',
  confidenceRange: 'all',
  status: 'all',
  sortBy: 'code',
  sortOrder: 'asc',
};

const selectClass = 'h-8 rounded-md border border-rule bg-transparent px-2 text-[13.5px] text-ink-2';

/**
 * `/mapari/:id` — live progress (while queued|processing), summary chips,
 * filter controls (Task 9), the virtualized status-grouped results table +
 * inline export endbar (Task 10), and (Task 3) a Tabel|Validare segmented
 * toggle that swaps the body below the verdict prose for the guided
 * `WizardShell` wizard.
 */
export function MaparePage() {
  const { id: idParam } = useParams();
  const id = Number(idParam);
  const location = useLocation();
  const navigate = useNavigate();
  /** T3: the Tabel|Validare page-level view switch — `/mapari/:id/validare(/:step)?`
   *  renders the wizard shell below instead of the filters+table+export block. */
  const isValidare = location.pathname.includes('/validare');
  const { data: run, isPending, isError } = useRun(id);

  /**
   * Run-outcome screen-reader announcement (fix round, 2026-08-20 — Task 3
   * follow-up). Owned here, not by `LiveProgress`: that component only
   * renders while `run.status` is `queued`/`processing` (this page's own
   * `isProcessing` gate below unmounts it the instant the run leaves that
   * state), so it can never itself observe a transition INTO `completed` —
   * by the time that status exists, `LiveProgress` is already gone. Watching
   * `run?.status` here instead works regardless of what child components are
   * mounted, because `MaparePage` itself stays mounted for the run's whole
   * lifetime on this route.
   *
   * `prevStatusRef` starts at `undefined` and is compared, not just read, so
   * that opening an ALREADY-finished run's page fresh (`undefined` →
   * `'completed'` on the very first render with data) does not announce —
   * only a transition observed live during this page's lifetime does.
   */
  const [runOutcome, setRunOutcome] = useState<{ text: string; politeness: 'polite' | 'assertive' }>({
    text: '',
    politeness: 'polite',
  });
  const prevStatusRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const prevStatus = prevStatusRef.current;
    const status = run?.status;
    prevStatusRef.current = status;
    if (prevStatus === undefined || status === undefined || prevStatus === status) return;
    if (status === 'completed') setRunOutcome({ text: 'Maparea s-a încheiat.', politeness: 'polite' });
    else if (status === 'failed') setRunOutcome({ text: 'Maparea a eșuat.', politeness: 'assertive' });
    else if (status === 'cancelled') setRunOutcome({ text: 'Maparea a fost anulată.', politeness: 'polite' });
  }, [run?.status]);

  const { data: wallet, isPending: walletPending } = useWallet();
  const { data: me } = useMe();
  // Billing-privacy: only owners/admins/platform admins see EUR. Members
  // never see the "Cost mapare" figure (label or „—") — see MaparePage's
  // costDisplay usage below.
  const billing = canSeeBilling(me);
  // Task 8: only polled/read while a run is queued — `useActiveSession`
  // already dedupes against Panou/RetryBar's own subscription via the
  // shared TanStack Query cache, so this doesn't add an extra request.
  const { data: activeSession } = useActiveSession();
  const finalizeRun = useFinalizeRun();
  const restartRun = useRestartRunWithInstance();
  const [filters, setFilters] = useState<FilterOptions>(DEFAULT_FILTERS);
  /** The review band used to stack 9 controls in one row (5 tabs + search + 2
   *  selects + a sort toggle) — over the working-memory budget on the screen the
   *  operator spends hours in. Confidence + sort now live behind this disclosure;
   *  the label says „active" (text, not colour alone) when either is off-default
   *  so a hidden filter can never silently narrow the table. */
  const [showAdvanced, setShowAdvanced] = useState(false);
  const advancedActive =
    filters.confidenceRange !== DEFAULT_FILTERS.confidenceRange ||
    filters.sortBy !== DEFAULT_FILTERS.sortBy ||
    filters.sortOrder !== DEFAULT_FILTERS.sortOrder;

  const rows = useMemo(() => (run ? buildRowsFromRun(run) : []), [run]);
  const visibleRows = useMemo(() => filterAndSortRows(rows, filters), [rows, filters]);

  /** Plan 3c-3 Task 6: `RunDetail.finalizedAt` (camelCase, T1) — a finalized run
   *  locks its entries server-side (T2 409s edits), so the table below goes
   *  read-only once this is true. */
  const finalized = Boolean(run?.finalizedAt);
  const finalizePending = finalizeRun.isPending && finalizeRun.variables?.id === run?.id;

  /**
   * Actual per-run cost = duration (startedAt → finishedAt) × org's hourly
   * rate (`runActualCostEur`, T3). `run` is already the camelCase
   * `{startedAt, finishedAt}` shape the helper takes directly. `hourlyPriceEur`
   * defaults to 0 while the wallet is still loading — `costDisplay` below
   * guards on `walletPending` separately so a 0-rate org is never confused
   * with "still loading".
   */
  const hourlyPriceEur = wallet?.hourlyPriceEur ?? 0;
  const cost = run ? runActualCostEur(run, hourlyPriceEur) : 0;
  /** „—" while the wallet hasn't loaded, the run hasn't finished yet, or the
   *  computed cost is 0 (unstarted/clock-skew) — a bare „0,00 €" would read
   *  as a priced, completed run rather than "no cost data yet". */
  const costDisplay =
    walletPending || !wallet || !run?.finishedAt || cost === 0 ? '—' : formatCurrencyEurRo(cost);

  /**
   * Final-review fix (Plan 3c-2): the summary chips MUST agree with the
   * table's section buckets by construction — both are now derived from the
   * same `groupResults` call over the same (unfiltered) `rows`, rather than
   * `calculateStats`'s separate predicates (which could disagree with
   * `groupResults`, silently dropping rows from every chip — e.g. a bulk
   * "Fără corespondent" row landed in that GROUP but in ZERO chips). Pending
   * rows are excluded from all four buckets by design (`LiveProgress` covers
   * them), so excluding them from the denominator here is correct too.
   */
  const grouped = useMemo(() => groupResults(rows), [rows]);
  /** Same population as the table's four sections (`groupResults` partitions
   *  every eligible row into exactly one), so this equals `progress.total` —
   *  reuse it rather than re-summing the buckets by hand. */
  const progress = useMemo(() => runProgress(rows), [rows]);
  const denom = progress.total;

  const isProcessing = run?.status === 'queued' || run?.status === 'processing';
  /** Task 8 step 2: still `queued` (Task 6's readiness gate holds it there)
   *  AND the org's instance is still coming up — swap `LiveProgress`'s bar
   *  for a "Se pornește instanța…" banner instead of a stalled 0/N. */
  const showProvisioningBanner = run ? shouldShowProvisioningBanner(run.status, activeSession?.provisioningState) : false;
  /** A distinct, recognizable failure (`failQueuedRunsForOrg`, server-side):
   *  the run was still `queued` when its org's Fleet rental failed/timed out,
   *  so it was marked `failed` with this fixed reason rather than sitting
   *  queued forever waiting for an instance that's never coming. Offers a
   *  retry CTA (`useRestartRun`, already used by `MapariListPage`'s generic
   *  "Repornire") instead of the bare failed state this view otherwise shows. */
  const provisioningFailed = run?.status === 'failed' && run.errorMessage === PROVISION_FAILED_REASON;
  const restartPending = restartRun.isPending && restartRun.variables === run?.id;
  const barPct = (n: number) => (denom > 0 ? (n / denom) * 100 : 0);

  return (
    <>
      <LiveRegion message={runOutcome.text} politeness={runOutcome.politeness} />
      <DocTop backTo="/mapari" backLabel="Mapări" />
      <div className="mt-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            {isPending ? (
              <div className="h-9 w-72 animate-pulse rounded-md bg-wash" aria-hidden />
            ) : (
              <h1 className="truncate text-[32px] font-[650] text-ink" title={run?.filename}>
                {run?.filename ?? 'Mapare'}
              </h1>
            )}
            {/* Locking a mapare is the peak-end of a multi-hour review session, so
                it states what was accomplished — when it was closed, how much got
                resolved, what it cost — not just that editing is off. */}
            {finalized && run && (
              <p className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[12.5px] text-ink-3">
                <span className="inline-flex items-center gap-1.5 font-medium text-ink-2">
                  <span className="size-2 shrink-0 rounded-full bg-ink-3" aria-hidden />
                  Blocată pentru editare
                </span>
                {run.finalizedAt && <>· {formatDateTimeRo(run.finalizedAt)}</>}
                · <span className="font-medium tabular-nums text-ink-2">{formatNumberRo(progress.validated)}</span> validate
                {' '}din <span className="font-medium tabular-nums text-ink-2">{formatNumberRo(progress.classified)}</span> clasificate
                {billing && <>· {costDisplay}</>}
                · Redeschideți pentru a edita.
              </p>
            )}
          </div>
          {run && !isProcessing && (
            <div className="flex shrink-0 items-center gap-4">
              {rows.length > 0 && (
                <Button size="sm" onClick={() => navigate(`/mapari/${run.id}/export`)}>
                  <Download />
                  Export
                </Button>
              )}
              {/* Finalize only makes sense for a completed run with results — a
                  failed/cancelled/empty run has nothing to lock. */}
              {run.status === 'completed' && rows.length > 0 && (
                <button
                  type="button"
                  onClick={() => finalizeRun.mutate({ id: run.id, finalized: !finalized })}
                  disabled={finalizePending}
                  className="text-[13.5px] font-medium text-teal hover:underline underline-offset-2 disabled:opacity-50 disabled:pointer-events-none disabled:no-underline"
                >
                  {finalized
                    ? finalizePending
                      ? 'Se redeschide…'
                      : 'Redeschide'
                    : finalizePending
                      ? 'Se blochează…'
                      : 'Blochează editarea'}
                </button>
              )}
            </div>
          )}
        </div>
        <div className="mt-1 border-b-2 border-ink pb-3" />

        {/* T5: Validare is the default review surface (`/mapari/:id` redirects
            here); this toggle's other side is the full unfiltered table, not
            a peer review mode — relabelled „Toate rândurile" accordingly, at
            its own `/tabel` route so it stays reachable and linkable. */}
        {run && !isProcessing && rows.length > 0 && (
          <div className="mt-4 inline-flex gap-1 rounded-md border border-rule p-0.5 text-[13.5px] font-medium">
            <button
              type="button"
              onClick={() => navigate(`/mapari/${run.id}/tabel`)}
              aria-pressed={!isValidare}
              className={!isValidare ? 'rounded px-3 py-1 bg-wash text-ink' : 'rounded px-3 py-1 text-ink-3 hover:text-ink-2'}
            >
              Toate rândurile
            </button>
            <button
              type="button"
              onClick={() => navigate(`/mapari/${run.id}/validare`)}
              aria-pressed={isValidare}
              className={isValidare ? 'rounded px-3 py-1 bg-wash text-ink' : 'rounded px-3 py-1 text-ink-3 hover:text-ink-2'}
            >
              Validare
            </button>
          </div>
        )}

        {isError && <p className="mt-6 text-[13.5px] text-bad">Nu s-a putut încărca maparea.</p>}

        {isPending && !isError && (
          <div className="mt-6 space-y-3">
            <div className="h-4 w-full animate-pulse rounded-md bg-wash" aria-hidden />
            <div className="h-4 w-2/3 animate-pulse rounded-md bg-wash" aria-hidden />
          </div>
        )}

        {run && provisioningFailed && (
          <p className="mt-6 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13.5px] text-bad">
            <span className="size-1.5 shrink-0 rounded-full bg-bad" aria-hidden />
            Maparea nu a pornit — instanța nu a putut fi pornită la timp.
            <button
              type="button"
              onClick={() => restartRun.run(run.id)}
              disabled={restartPending}
              className="font-medium text-teal hover:underline disabled:pointer-events-none disabled:opacity-50"
            >
              {restartPending ? 'Se reîncearcă…' : 'Reîncearcă'}
            </button>
          </p>
        )}

        {run && (
          showProvisioningBanner ? (
            /* Provisioning takes 2–5 min; make it unmissable and replace the whole
               results form (verdict + filters + empty table + export) so the operator
               isn't left staring at a 0/0 table wondering if it stalled. Task 6's
               readiness gate keeps the run `queued` meanwhile; `onReady` kicks the
               runner and this panel gives way to `LiveProgress` automatically. */
            <div className="mt-8 rounded-xl border border-warn/30 bg-warn/5 px-6 py-12 text-center sm:py-16">
              <span className="relative mx-auto flex size-5">
                <span className="absolute inline-flex size-full rounded-full bg-warn opacity-60 motion-safe:animate-ping" aria-hidden />
                <span className="relative inline-flex size-5 rounded-full bg-warn" aria-hidden />
              </span>
              <h2 className="mt-6 text-[20px] font-[650] text-ink">Se pornește instanța de calcul…</h2>
              <p className="mx-auto mt-2 max-w-md text-[15px] text-ink-2">
                {rows.length === 1 ? (
                  'Maparea unui rând'
                ) : (
                  <>
                    Maparea celor <span className="font-semibold tabular-nums">{formatNumberRo(rows.length)}</span> rânduri
                  </>
                )}
                {' '}începe automat imediat ce instanța este gata — de obicei în 2–5 minute.
              </p>
              <p className="mx-auto mt-2 max-w-md text-[13.5px] text-ink-3">
                Nu trebuie să faceți nimic. Puteți lăsa pagina deschisă — progresul apare aici.
              </p>
            </div>
          ) : (
          <>
            {isProcessing && <LiveProgress run={run} />}

            {/* T3: Validare renders the wizard shell instead of the filters +
                table + export block below — same `rows`/`finalized`, no refetch. */}
            {isValidare && <WizardShell rows={rows} runId={run.id} finalized={finalized} />}

            {!isValidare && (
              <>
                {rows.length === 0 && !isProcessing ? (
                  <p className="mt-6 text-[13.5px] text-ink-3">Nicio linie de clasificat încă.</p>
                ) : (
                  <>
                    {/* Summary card (hybrid redesign): DESIGN.md „Verdict prose"
                        (one Romanian sentence stating both numbers) + a stacked
                        distribution bar (ok/warn/mute/bad proportions) + the run
                        cost (the card's one remaining lead figure) + the status
                        counts as filter chips. */}
                    <div className="mt-6">
                      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-6">
                        <VerdictLine progress={progress} />
                        {billing && (
                          <div className="whitespace-nowrap text-right">
                            <span className="block text-[11.5px] font-medium uppercase tracking-[0.06em] text-ink-3">Cost mapare</span>
                            <span className="mt-0.5 block text-[26px] font-[650] tabular-nums text-ink">{costDisplay}</span>
                          </div>
                        )}
                      </div>
                      <div className="mt-4 flex h-2.5 overflow-hidden rounded-full bg-rule-soft" role="img" aria-label="Distribuția stărilor">
                        <span className="bg-ok" style={{ width: `${barPct(grouped.rezolvate.length)}%` }} />
                        <span className="bg-warn" style={{ width: `${barPct(grouped.deVerificat.length)}%` }} />
                        <span className="bg-mute" style={{ width: `${barPct(grouped.faraCorespondent.length)}%` }} />
                        <span className="bg-bad" style={{ width: `${barPct(grouped.erori.length)}%` }} />
                      </div>
                      <div className="mt-3 flex flex-wrap gap-1">
                        {STATUS_TABS.map((tab) => {
                          const count =
                            tab.value === 'all'
                              ? denom
                              : tab.value === 'correct'
                                ? grouped.rezolvate.length
                                : tab.value === 'review_pending'
                                  ? grouped.deVerificat.length
                                  : tab.value === 'unresolved'
                                    ? grouped.faraCorespondent.length
                                    : grouped.erori.length;
                          const dot =
                            tab.value === 'correct'
                              ? 'bg-ok'
                              : tab.value === 'review_pending'
                                ? 'bg-warn'
                                : tab.value === 'unresolved'
                                  ? 'bg-mute'
                                  : tab.value === 'error'
                                    ? 'bg-bad'
                                    : 'bg-ink-3';
                          const active = filters.status === tab.value;
                          return (
                            <button
                              key={tab.value}
                              type="button"
                              onClick={() => setFilters((f) => ({ ...f, status: tab.value }))}
                              aria-pressed={active}
                              className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-[13.5px] font-medium transition-colors ${
                                active ? 'border-rule bg-wash text-ink' : 'border-transparent text-ink-2 hover:bg-wash'
                              }`}
                            >
                              <span className={`size-2 shrink-0 rounded-full ${dot}`} aria-hidden />
                              {tab.label}
                              <span className={`tabular-nums ${count === 0 ? 'text-ink-3' : 'font-semibold text-ink'}`}>{formatNumberRo(count)}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Toolbar: search + advanced-filter disclosure. */}
                    <div className="mt-4 flex flex-wrap items-center gap-3">
                      <div className="min-w-[220px] flex-1">
                        <Input
                          type="text"
                          value={filters.searchTerm}
                          onChange={(e) => setFilters((f) => ({ ...f, searchTerm: e.target.value }))}
                          placeholder="Caută după cod sau denumire…"
                          className="h-9 w-full"
                          aria-label="Caută după cod sau denumire"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowAdvanced((v) => !v)}
                        aria-expanded={showAdvanced}
                        className="inline-flex h-9 items-center gap-2 rounded-md border border-rule px-3 text-[13.5px] font-medium text-ink-2 hover:bg-wash"
                      >
                        {advancedActive && <span className="size-1.5 rounded-full bg-teal" aria-hidden />}
                        Filtre{advancedActive ? ' · active' : ''}
                      </button>
                    </div>

                    {showAdvanced && (
                      <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-rule-soft pt-3">
                        <select
                          value={filters.confidenceRange}
                          onChange={(e) =>
                            setFilters((f) => ({
                              ...f,
                              confidenceRange: e.target.value as FilterOptions['confidenceRange'],
                            }))
                          }
                          className={selectClass}
                          aria-label="Filtrează după încredere"
                        >
                          {CONFIDENCE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                        <select
                          value={filters.sortBy}
                          onChange={(e) =>
                            setFilters((f) => ({ ...f, sortBy: e.target.value as FilterOptions['sortBy'] }))
                          }
                          className={selectClass}
                          aria-label="Sortează după"
                        >
                          {SORT_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() =>
                            setFilters((f) => ({ ...f, sortOrder: f.sortOrder === 'asc' ? 'desc' : 'asc' }))
                          }
                          className="text-[13.5px] font-medium text-ink-3 hover:text-ink-2"
                        >
                          {filters.sortOrder === 'asc' ? 'Crescător ↑' : 'Descrescător ↓'}
                        </button>
                      </div>
                    )}

                    <p className="mt-3 tabular-nums text-[12.5px] text-ink-3">
                      {formatNumberRo(visibleRows.length)} din {formatNumberRo(rows.length)} rânduri afișate
                    </p>
                  </>
                )}

                <ResultsTable
                  rows={visibleRows}
                  runId={run.id}
                  readOnly={finalized}
                  rezolvateFilterActive={filters.status === 'correct'}
                />
              </>
            )}
          </>
          )
        )}
      </div>
    </>
  );
}
